/* istanbul ignore file */
/* tslint:disable */
 
import type { CascoCoefficient } from '../models/CascoCoefficient';
import type { CascoCoefficientBulk } from '../models/CascoCoefficientBulk';
import type { CascoTariff } from '../models/CascoTariff';
import type { CascoTariffBulk } from '../models/CascoTariffBulk';
import type { PaginatedCascoBorderoList } from '../models/PaginatedCascoBorderoList';
import type { PatchedCascoCoefficient } from '../models/PatchedCascoCoefficient';
import type { PatchedCascoTariff } from '../models/PatchedCascoTariff';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CascoService {

  /**
   * Bordero export for CASCO insurance.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: order__policy__number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns PaginatedCascoBorderoList 
   * @throws ApiError
   */
  public static cascoBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedCascoBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Bordero export for CASCO insurance.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search Search fields: order__policy__number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static cascoBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * bulk update
 * by unique together fields
 * with log
   * @param name * `contractor_type` - Client type
 * * `territory` - Territoriality
 * * `driver_age` - Driver age
 * * `driving_experience` - Driving experience
 * * `parking_mode` - Parking mode
 * * `quantity_discount` - Quantity discount
 * * `full_payment_discount` - Full payment discount
 * * `premium_brand` - Premium brand
 * * `made_in_china` - Made in China
 * * `claim_last_year` - Rata daunei anul anterior >100%
 * * `official_dealer_repair` - Official Dealer Repair (>3 years)
 * * `insurance_period` - Insurance period
 * * `mobility` - Mobility. Replacement vehicle
   * @param ordering Ordering fields: name, variable, value
   * @returns CascoCoefficient 
   * @throws ApiError
   */
  public static cascoCoefficientsList(
name?: 'claim_last_year' | 'contractor_type' | 'driver_age' | 'driving_experience' | 'full_payment_discount' | 'insurance_period' | 'made_in_china' | 'mobility' | 'official_dealer_repair' | 'parking_mode' | 'premium_brand' | 'quantity_discount' | 'territory',
ordering?: string,
): CancelablePromise<Array<CascoCoefficient>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/coefficients/',
      query: {
        'name': name,
        'ordering': ordering,
      },
    });
  }

  /**
   * bulk update
 * by unique together fields
 * with log
   * @param id A unique integer value identifying this casco coefficient.
   * @returns CascoCoefficient 
   * @throws ApiError
   */
  public static cascoCoefficientsRetrieve(
id: number,
): CancelablePromise<CascoCoefficient> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/coefficients/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * bulk update
 * by unique together fields
 * with log
   * @param id A unique integer value identifying this casco coefficient.
   * @param requestBody 
   * @returns CascoCoefficient 
   * @throws ApiError
   */
  public static cascoCoefficientsPartialUpdate(
id: number,
requestBody?: PatchedCascoCoefficient,
): CancelablePromise<CascoCoefficient> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/casco/coefficients/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * bulk update
 * by unique together fields
 * with log
   * @param requestBody 
   * @param name * `contractor_type` - Client type
 * * `territory` - Territoriality
 * * `driver_age` - Driver age
 * * `driving_experience` - Driving experience
 * * `parking_mode` - Parking mode
 * * `quantity_discount` - Quantity discount
 * * `full_payment_discount` - Full payment discount
 * * `premium_brand` - Premium brand
 * * `made_in_china` - Made in China
 * * `claim_last_year` - Rata daunei anul anterior >100%
 * * `official_dealer_repair` - Official Dealer Repair (>3 years)
 * * `insurance_period` - Insurance period
 * * `mobility` - Mobility. Replacement vehicle
   * @param ordering Ordering fields: name, variable, value
   * @returns CascoCoefficientBulk 
   * @throws ApiError
   */
  public static cascoCoefficientsBulkUpdateCreate(
requestBody: Array<CascoCoefficientBulk>,
name?: 'claim_last_year' | 'contractor_type' | 'driver_age' | 'driving_experience' | 'full_payment_discount' | 'insurance_period' | 'made_in_china' | 'mobility' | 'official_dealer_repair' | 'parking_mode' | 'premium_brand' | 'quantity_discount' | 'territory',
ordering?: string,
): CancelablePromise<Array<CascoCoefficientBulk>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/casco/coefficients/bulk-update/',
      query: {
        'name': name,
        'ordering': ordering,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Base tariff taxes
   * @param franchise partial franchise type
 *
 * * `zero` - 0 EUR
 * * `non_zero` - Var.1 = 1%, Var.2 = 10%
   * @param insuredAmount * `0` - ≤ 10 000 EUR
 * * `10000` - 10 001 - 20 000 EUR
 * * `30000` - >30 001 EUR
 * * `20000` - 20 001 - 50 000 EUR
 * * `50000` - > 50 001 EUR
 * * `25000` - Amount 250000
 * * `75000` - Amount 750000
   * @param ordering Ordering fields: package, vehicle_category, franchise, insured_amount
   * @param _package * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param vehicleCategory * `automobile` - Autoturisme și microbuze ≤8 locuri
 * * `truck_light` - Camioane ≤3500 kg
 * * `truck_heavy` - Camioane >3500 kg
 * * `bus` - Autobuze și microbuze >20 locuri
 * * `trailer` - Remorci și semiremorci
 * * `tractor` - Tractoare și tehnică agricolă
 * * `motorcycle` - Motociclete
   * @returns CascoTariff 
   * @throws ApiError
   */
  public static cascoTariffsList(
franchise?: 'non_zero' | 'zero' | null,
insuredAmount?: 0 | 10000 | 20000 | 25000 | 30000 | 50000 | 75000 | null,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
vehicleCategory?: 'automobile' | 'bus' | 'motorcycle' | 'tractor' | 'trailer' | 'truck_heavy' | 'truck_light',
): CancelablePromise<Array<CascoTariff>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/tariffs/',
      query: {
        'franchise': franchise,
        'insured_amount': insuredAmount,
        'ordering': ordering,
        'package': _package,
        'vehicle_category': vehicleCategory,
      },
    });
  }

  /**
   * Base tariff taxes
   * @param id A unique integer value identifying this casco tariff.
   * @returns CascoTariff 
   * @throws ApiError
   */
  public static cascoTariffsRetrieve(
id: number,
): CancelablePromise<CascoTariff> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/casco/tariffs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Base tariff taxes
   * @param id A unique integer value identifying this casco tariff.
   * @param requestBody 
   * @returns CascoTariff 
   * @throws ApiError
   */
  public static cascoTariffsPartialUpdate(
id: number,
requestBody?: PatchedCascoTariff,
): CancelablePromise<CascoTariff> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/casco/tariffs/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Base tariff taxes
   * @param requestBody 
   * @param franchise partial franchise type
 *
 * * `zero` - 0 EUR
 * * `non_zero` - Var.1 = 1%, Var.2 = 10%
   * @param insuredAmount * `0` - ≤ 10 000 EUR
 * * `10000` - 10 001 - 20 000 EUR
 * * `30000` - >30 001 EUR
 * * `20000` - 20 001 - 50 000 EUR
 * * `50000` - > 50 001 EUR
 * * `25000` - Amount 250000
 * * `75000` - Amount 750000
   * @param ordering Ordering fields: package, vehicle_category, franchise, insured_amount
   * @param _package * `standard` - CASCO "STANDARD"
 * * `comfort` - CASCO "CONFORT"
 * * `fifty_fifty` - CASCO "50/50"
 * * `mini` - CASCO "MINI"
   * @param vehicleCategory * `automobile` - Autoturisme și microbuze ≤8 locuri
 * * `truck_light` - Camioane ≤3500 kg
 * * `truck_heavy` - Camioane >3500 kg
 * * `bus` - Autobuze și microbuze >20 locuri
 * * `trailer` - Remorci și semiremorci
 * * `tractor` - Tractoare și tehnică agricolă
 * * `motorcycle` - Motociclete
   * @returns CascoTariffBulk 
   * @throws ApiError
   */
  public static cascoTariffsBulkUpdateCreate(
requestBody: Array<CascoTariffBulk>,
franchise?: 'non_zero' | 'zero' | null,
insuredAmount?: 0 | 10000 | 20000 | 25000 | 30000 | 50000 | 75000 | null,
ordering?: string,
_package?: 'comfort' | 'fifty_fifty' | 'mini' | 'standard',
vehicleCategory?: 'automobile' | 'bus' | 'motorcycle' | 'tractor' | 'trailer' | 'truck_heavy' | 'truck_light',
): CancelablePromise<Array<CascoTariffBulk>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/casco/tariffs/bulk-update/',
      query: {
        'franchise': franchise,
        'insured_amount': insuredAmount,
        'ordering': ordering,
        'package': _package,
        'vehicle_category': vehicleCategory,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
